import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/store/modules/user';
import { ENV_CONFIG } from '@/config/env';
import { i18n } from '@/locales';
import { ProductId } from '../enum/account';

export interface ApiResp<T=unknown> { code: string | number; data: T; message?: string; }

// 根据环境配置基础 URL
const getBaseURL = () => {
  // 开发环境使用代理，不添加前缀
  if (import.meta.env.DEV) {
    return '/lincid';
  }
  
  // 其他环境使用配置的 API 地址
  return ENV_CONFIG.API_BASE_URL;
};

const service: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: ENV_CONFIG.API_TIMEOUT
});

// 用于防止重复提示 token 过期
let isShowingTokenExpiredMessage = false;

service.interceptors.request.use((config: any) => {
  const user = useUserStore();
  
  // 设置默认请求头
  config.headers = config.headers || {};
  
  // 如果是 FormData，不设置 Content-Type，让浏览器自动设置（包括 boundary）
  // 否则设置默认的 application/json
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  // 设置语言请求头
  const currentLocale = typeof i18n.global.locale === 'string' ? i18n.global.locale : i18n.global.locale.value;
  if (currentLocale) {
    config.headers['Linc-i18nextLng'] = currentLocale;
  }
  
  // 添加 token
  if (user.token) {
    (config.headers as any).Authorization = `Bearer ${user.token}`;
  }
  if (user.userEnterpriseId) {
    (config.headers as any)['x-enterprise-id'] = `${user.userEnterpriseId}`;
  }
  if (!config.noProductId) {
    // 添加 token
    if (user.token) {
      const productId = user.productId;
      (config.headers as any)['x-product-id'] = productId;
    }
  }
  // 开发环境打印请求信息
  if (import.meta.env.DEV) {
    // console.log('📤 API 请求:', {
    //   method: config.method?.toUpperCase(),
    //   url: config.url,
    //   baseURL: config.baseURL,
    //   fullURL: (config.baseURL || '') + (config.url || ''),
    //   headers: {
    //     Authorization: config.headers?.Authorization ? '***' : undefined,
    //     'x-enterprise-id': config.headers?.['x-enterprise-id'],
    //     'Linc-i18nextLng': config.headers?.['Linc-i18nextLng']
    //   },
    //   data: config.data instanceof FormData ? '[FormData]' : config.data
    // });
  }
  
  return config;
});

service.interceptors.response.use(
  (res: AxiosResponse<ApiResp>) => {
    // 开发环境打印响应信息
    if (import.meta.env.DEV) {
      // console.log('📥 API 响应:', {
      //   status: res.status,
      //   statusText: res.statusText,
      //   url: res.config.url,
      //   dataType: res.data instanceof Blob ? 'Blob' : typeof res.data,
      //   data: res.data instanceof Blob ? '[Blob]' : res.data
      // });
    }
    
    // 如果响应是 Blob 类型（文件下载），返回包含 headers 的响应对象
    if (res.data instanceof Blob) {
      return {
        data: res.data,
        headers: res.headers
      } as any;
    }
    
    const payload = res.data;
    const code = String(payload.code);
    
    // 判断是否为成功状态
    const isSuccess = code === '00000' || code === '0' || code === '200';
    
    if (!isSuccess) {
      const user = useUserStore();
      
      // Token 过期处理
      if (code === 'C0007' || code === '00002' || code === 'C0012') {
        if (!isShowingTokenExpiredMessage) {
          isShowingTokenExpiredMessage = true;
          // if (code === 'C0012') {
          //   ElMessage.info(i18n.global.t('common.authChange'));
          // } else {
          //   ElMessage.info(i18n.global.t('common.tokenExpired'));
          // }
          user.logout(code);
        }
        return Promise.reject(new Error(payload.message || 'Token expired'));
      }
      
      // 如果是 401 错误，直接登出
      if (code === '401' || payload.code === 401) {
        user.logout();
      }
      
      return Promise.reject(payload);
    }
    
    // 成功时返回 data
    return payload.data as any;
  },
  (err) => {
    // 开发环境打印错误信息
    if (import.meta.env.DEV) {
      console.error('请求错误:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        data: err.response?.data,
        message: err.message
      });
    }
    
    const user = useUserStore();
    
    // 检查响应数据中的 code
    const responseData = err.response?.data;
    if (responseData && responseData.code !== undefined) {
      const code = String(responseData.code);
      
      // Token 过期处理
      if (code === 'C0007' || code === '00002' || code === 'C0012') {
        if (!isShowingTokenExpiredMessage) {
          isShowingTokenExpiredMessage = true;
          // if (code === 'C0012') {
          //   ElMessage.info(i18n.global.t('common.authChange'));
          // } else {
          //   ElMessage.info(i18n.global.t('common.tokenExpired'));
          // }
          user.logout(code);
        }
        return Promise.reject(new Error(responseData.message || 'Token expired'));
      }
    }
    
    // 如果是 401 错误，直接登出
    if (err.response?.status === 401) {
      user.logout();
    }
    
    // 处理超时错误
    if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      ElMessage.error(i18n.global.t('common.requestTimeout'));
      return Promise.reject(new Error(i18n.global.t('common.requestTimeout')));
    }
    
    // 处理网络错误
    if (err.message === 'Network Error' || !err.response) {
      ElMessage.error(i18n.global.t('common.networkError'));
      return Promise.reject(new Error(i18n.global.t('common.networkError')));
    }
    
    return Promise.reject(err);
  }
);

export function request<T=any>(config: AxiosRequestConfig & {noProductId?: boolean}) {
  return service.request<any, T>(config);
}

export default service;
